import { useState } from 'react';
import { firebaseApp } from '../firebase';
import Handlebars from 'handlebars';
import { getFirestore, getDocs, QueryDocumentSnapshot, DocumentData } from 'firebase/firestore';
import { useBuildQuery } from '../components/listings/hooks/use-listing-query';
import { flattenFlexSubmissionData, SubmissionData } from './flexFormUtils';

export interface TestResult {
  subject: string;
  body: string;
  story: string;
}

export interface UseTemplateTesterResult {
  testModalOpen: boolean;
  setTestModalOpen: (open: boolean) => void;
  testResult: TestResult | null;
  testing: boolean;
  testTemplates: (notificationSubjectLine: string, notificationBody: string, storyTemplate?: string) => Promise<void>;
  rawSubmission: Record<string, any> | null;
  flattenedData: Record<string, any> | null;
}

export function useTemplateTester(templateId: string, companyId: string): UseTemplateTesterResult {
  const [testModalOpen, setTestModalOpen] = useState(false);
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [testing, setTesting] = useState(false);
  const [rawSubmission, setRawSubmission] = useState<Record<string, any> | null>(null);
  const [flattenedData, setFlattenedData] = useState<Record<string, any> | null>(null);

  const db = getFirestore(firebaseApp);
  const buildQuery = useBuildQuery({
    db,
    companyId,
    pageSize: 1,
    collectionName: "flexFormSubmissions",
    dateField: "submissionDate",
  });

  const testTemplates = async (
    notificationSubjectLine: string,
    notificationBody: string,
    storyTemplate?: string
  ): Promise<void> => {
    setTesting(true);
    try {
      // Fetch a recent submission from flexFormSubmissions
      const activeFilters = { templateId: templateId };
      const filterConfig = [
        {
          paramName: "templateId",
          fieldIdToFilter: "templateId",
          label: "Template",
        },
      ];
      const q = buildQuery(undefined, null, activeFilters, filterConfig);
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        const submissionDoc = snapshot.docs[0];
        const submission = { id: submissionDoc.id, ...submissionDoc.data() } as unknown as SubmissionData;
        const flattened = flattenFlexSubmissionData(submission);
        const subjectRendered = Handlebars.compile(notificationSubjectLine)(flattened);
        const bodyRendered = Handlebars.compile(notificationBody)(flattened);
        const storyRendered = storyTemplate ? Handlebars.compile(storyTemplate)(flattened) : '';
        setTestResult({ subject: subjectRendered, body: bodyRendered, story: storyRendered });
        setRawSubmission(submission as any);
        setFlattenedData(flattened);
      } else {
        setTestResult({ subject: 'No recent submission found', body: '', story: '' });
        setRawSubmission(null);
        setFlattenedData(null);
      }
      setTestModalOpen(true);
    } catch (error: any) {
      console.error('Error testing templates:', error);
      setTestResult({ subject: 'Error testing', body: error.message || 'Error occurred', story: '' });
      setRawSubmission(null);
      setFlattenedData(null);
      setTestModalOpen(true);
    } finally {
      setTesting(false);
    }
  };

  return {
    testModalOpen,
    setTestModalOpen,
    testResult,
    testing,
    testTemplates,
    rawSubmission,
    flattenedData,
  };
}